import { useMemo } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { FolderOpen, Settings, Truck, CreditCard, Users } from 'lucide-react'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { Organization_ } from 'src/api/db/apiOrgs'
import { useGetOrgId } from 'src/api/helpers/getOrgId'
import { Column } from 'src/shared/components/Semantic/all'
import { useLocalization } from 'src/shared/hooks/useLocalization'

import { ShippingLocations } from 'src/shared/components/organizationPage/ShippingLocations'
import { PaymentSettings } from 'src/shared/components/organizationPage/PaymentSettings'
import { TeamSettings } from 'src/shared/components/organizationPage/TeamSettings'
import { TabNavigation, Tab } from 'src/shared/components/TabNavigation'
import { GeneralSettings } from 'src/shared/components/organizationPage/GeneralSettings'
import { AssetsPage } from 'src/pages/_myAccount/Assets'

type OrganizationSettingsTabsProps = {
  teamOrgId?: string
}

/** Settings URL tab ids (stable for bookmarks); legacy ids aliased below */
const TAB_GENERAL = 'general'
const TAB_SHIPPING = 'shipping-locations'
const TAB_TEAM = 'team'
const TAB_BILLING = 'billing-details'
const TAB_ASSETS = 'assets'

export function OrganizationSettingsTabs({
  teamOrgId,
}: OrganizationSettingsTabsProps) {
  const { t } = useLocalization()
  const org_id = useGetOrgId()
  const { organizationById } = useOrgsApi({ org_id })
  const organization = organizationById?.data
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  const tabs: Tab[] = [
    {
      id: TAB_GENERAL,
      label: t('General', 'Allmänt'),
      icon: <Settings className="h-4 w-4" />,
    },
    {
      id: TAB_SHIPPING,
      label: t('Shipping locations', 'Leveransadresser'),
      icon: <Truck className="h-4 w-4" />,
    },
    {
      id: TAB_TEAM,
      label: t('Team', 'Team'),
      icon: <Users className="h-4 w-4" />,
    },
    {
      id: TAB_BILLING,
      label: t('Billing details', 'Faktureringsinformation'),
      icon: <CreditCard className="h-4 w-4" />,
    },
    {
      id: TAB_ASSETS,
      label: t('Assets', 'Filer'),
      icon: <FolderOpen className="h-4 w-4" />,
    },
  ]

  const validTabIds = useMemo(() => tabs.map((tab) => tab.id), [tabs])

  const urlTab = searchParams.get('tab')
  const rawTab =
    urlTab === 'brand'
      ? null
      : urlTab === 'payments'
        ? TAB_BILLING
        : urlTab

  if (urlTab === 'brand') {
    const stripped = new URLSearchParams(searchParams)
    stripped.delete('tab')
    const nextSearch = stripped.toString()
    return (
      <Navigate
        to={{
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        }}
        replace
      />
    )
  }

  const activeTab =
    rawTab && validTabIds.includes(rawTab) ? rawTab : TAB_GENERAL

  const setActiveTab = (tabId: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (tabId === TAB_GENERAL) {
      newParams.delete('tab')
    } else {
      newParams.set('tab', tabId)
    }
    setSearchParams(newParams, { replace: true })
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case TAB_GENERAL:
        return <GeneralSettings organization={organization as Organization_} />
      case TAB_SHIPPING:
        return (
          <ShippingLocations organization={organization as Organization_} />
        )
      case TAB_BILLING:
        return <PaymentSettings organization={organization as Organization_} />
      case TAB_TEAM:
        return <TeamSettings org_id={teamOrgId} />
      case TAB_ASSETS:
        return <AssetsPage embedded />
      default:
        return null
    }
  }

  return (
    <Column className="gap-4">
      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <Column className="gap-4 mt-4">{renderTabContent()}</Column>
    </Column>
  )
}
